import { Dialog, Switch, Transition } from '@headlessui/react'
import { Fragment, useEffect, useState } from 'react'
import type { FormTemplateDetail, FormTemplateUpdatePayload } from '@/types'
import { FormType } from '@/types'

interface TemplateSettingsModalProps {
  open: boolean
  template?: FormTemplateDetail | null
  submitting?: boolean
  onClose: () => void
  onSubmit: (payload: FormTemplateUpdatePayload) => Promise<void>
}

const joinTags = (tags: string[] | undefined) => tags?.join(', ') ?? ''

const splitTags = (value: string) =>
  value
    .split(',')
    .map(tag => tag.trim())
    .filter(tag => tag.length > 0)

export function TemplateSettingsModal({ open, template, submitting = false, onClose, onSubmit }: TemplateSettingsModalProps) {
  const [formState, setFormState] = useState<FormTemplateUpdatePayload>({})
  const [tagInput, setTagInput] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!template) return
    setFormState({
      name: template.name,
      description: template.description ?? '',
      projectId: template.projectId ?? null,
      ownerId: template.ownerId ?? null,
      tags: template.tags ?? [],
      archived: template.archived ?? false,
      formType: template.formType,
      activationStartDate: template.activationStartDate ? template.activationStartDate.slice(0, 10) : '',
      activationEndDate: template.activationEndDate ? template.activationEndDate.slice(0, 10) : '',
      isAlwaysActive: template.isAlwaysActive ?? false,
    })
    setTagInput(joinTags(template.tags))
    setError(null)
  }, [template, open])

  const handleClose = () => {
    if (submitting) return
    setError(null)
    onClose()
  }

  const handleSubmit = async () => {
    if (!formState.name?.trim()) {
      setError('模板名稱不可為空')
      return
    }
    try {
      const activationStartDate = formState.isAlwaysActive
        ? null
        : formState.activationStartDate
          ? formState.activationStartDate.trim() || null
          : null
      const activationEndDate = formState.isAlwaysActive
        ? null
        : formState.activationEndDate
          ? formState.activationEndDate.trim() || null
          : null

      await onSubmit({
        name: formState.name.trim(),
        description: formState.description?.trim() ?? null,
        projectId: formState.projectId ?? null,
        ownerId: formState.ownerId ?? null,
        tags: splitTags(tagInput),
        archived: formState.archived,
        formType: formState.formType,
        activationStartDate,
        activationEndDate,
        isAlwaysActive: formState.isAlwaysActive ?? false,
      })
      setError(null)
      onClose()
    } catch (err) {
      const message = err instanceof Error ? err.message : '更新模板設定失敗'
      setError(message)
    }
  }

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Dialog.Panel className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
                <Dialog.Title className="text-lg font-semibold text-gray-900">模板設定</Dialog.Title>

                <div className="mt-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">模板名稱</label>
                    <input
                      type="text"
                      value={formState.name ?? ''}
                      onChange={(e) => setFormState(prev => ({ ...prev, name: e.target.value }))}
                      className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">描述</label>
                    <textarea
                      value={formState.description ?? ''}
                      onChange={(e) => setFormState(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">標籤（以逗號分隔）</label>
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                      placeholder="設備, 檢查, 日常"
                    />
                  </div>

                  <div className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                    <div>
                      <p className="text-sm font-medium text-gray-700">封存模板</p>
                      <p className="text-xs text-gray-500">封存後模板將在清單中隱藏，但仍可透過搜尋查詢。</p>
                    </div>
                    <Switch
                      checked={Boolean(formState.archived)}
                      onChange={(value: boolean) => setFormState(prev => ({ ...prev, archived: value }))}
                      className={`${formState.archived ? 'bg-primary-600' : 'bg-gray-200'} relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                    >
                      <span
                        className={`${formState.archived ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition`}
                      />
                    </Switch>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">範圍</label>
                    <p className="mt-1 text-sm text-gray-500">{template?.scopeType ?? '—'}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">表單型態</label>
                    <select
                      value={formState.formType ?? FormType.ONE_OFF}
                      onChange={(e) => setFormState(prev => ({ ...prev, formType: e.target.value as FormType }))}
                      className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                    >
                      <option value={FormType.ONE_OFF}>一次性表單</option>
                      <option value={FormType.PERIODIC}>定期檢查表</option>
                    </select>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">啟用開始日</label>
                      <input
                        type="date"
                        value={typeof formState.activationStartDate === 'string' ? formState.activationStartDate : ''}
                        onChange={(e) => setFormState(prev => ({ ...prev, activationStartDate: e.target.value }))}
                        disabled={Boolean(formState.isAlwaysActive)}
                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">啟用結束日</label>
                      <input
                        type="date"
                        value={typeof formState.activationEndDate === 'string' ? formState.activationEndDate : ''}
                        onChange={(e) => setFormState(prev => ({ ...prev, activationEndDate: e.target.value }))}
                        disabled={Boolean(formState.isAlwaysActive)}
                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-100"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                    <div>
                      <p className="text-sm font-medium text-gray-700">長駐啟用</p>
                      <p className="text-xs text-gray-500">開啟後會長期顯示於專案表單清單，忽略啟用時間設定。</p>
                    </div>
                    <Switch
                      checked={Boolean(formState.isAlwaysActive)}
                      onChange={(value: boolean) => setFormState(prev => ({
                        ...prev,
                        isAlwaysActive: value,
                        activationStartDate: value ? '' : (typeof prev.activationStartDate === 'string' ? prev.activationStartDate : ''),
                        activationEndDate: value ? '' : (typeof prev.activationEndDate === 'string' ? prev.activationEndDate : ''),
                      }))}
                      className={`${formState.isAlwaysActive ? 'bg-primary-600' : 'bg-gray-200'} relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                    >
                      <span
                        className={`${formState.isAlwaysActive ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition`}
                      />
                    </Switch>
                  </div>

                  {error && <p className="text-sm text-red-600">{error}</p>}
                </div>

                <div className="mt-6 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-100"
                    disabled={submitting}
                  >
                    取消
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="rounded bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-70"
                    disabled={submitting}
                  >
                    {submitting ? '儲存中…' : '儲存設定'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default TemplateSettingsModal
